import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import React, { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { CustomThemeProvider } from "./contexts/ThemeContext";
import ThemeAestheticsController from "./components/aesthetics/ThemeAestheticsController";
import Layout from "./components/Layout";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { isFeatureEnabled } from "@/lib/feature-routes";
import type { FeatureFlags } from "@/types/site-config";

// Loading fallback component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-foreground" />
  </div>
);

// Redirect components for old /mixes routes
const RedirectMixSlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/audio/${slug}`} replace />;
};

const RedirectMixEdit = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/audio/edit/${id}`} replace />;
};

// Redirect components for old /photos routes
const RedirectGallerySlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/galleries/${slug}`} replace />;
};

const RedirectPhotoDetail = () => {
  const { gallerySlug, photoId } = useParams();
  return <Navigate to={`/galleries/${gallerySlug}/${photoId}`} replace />;
};

// Redirect components for old /writing routes (renamed to /blog)
const RedirectWritingSlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
};

const RedirectWritingCategorySlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/categories/${slug}`} replace />;
};

const RedirectWritingCategoryPage = () => {
  const { slug, page } = useParams();
  return <Navigate to={`/blog/categories/${slug}/page/${page}`} replace />;
};

const RedirectWritingTagSlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/tags/${slug}`} replace />;
};

const RedirectWritingTagPage = () => {
  const { slug, page } = useParams();
  return <Navigate to={`/blog/tags/${slug}/page/${page}`} replace />;
};

// Lazy load all route components
const Index = lazy(() => import("./pages/Index"));
const Photos = lazy(() => import("./pages/Photos"));
const GalleryDetail = lazy(() => import("./pages/GalleryDetail"));
const PhotoDetail = lazy(() => import("./pages/PhotoDetail"));
const Writing = lazy(() => import("./pages/Writing"));
const Links = lazy(() => import("./pages/Links"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const CategoryIndex = lazy(() => import("./pages/CategoryIndex"));
const CategoryDetail = lazy(() => import("./pages/CategoryDetail"));
const TagIndex = lazy(() => import("./pages/TagIndex"));
const TagDetail = lazy(() => import("./pages/TagDetail"));
const Archive = lazy(() => import("./pages/Archive"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const SignIn = lazy(() => import("./pages/auth/SignIn"));
const SignOut = lazy(() => import("./pages/auth/SignOut"));
const Error = lazy(() => import("./pages/auth/Error"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const LinksAdmin = lazy(() => import("./pages/admin/LinksAdmin"));
const GalleryAdmin = lazy(() => import("./pages/admin/GalleryAdmin"));
const GalleryEdit = lazy(() => import("./pages/admin/GalleryEdit"));
const NewGallery = lazy(() => import("./pages/admin/galleries/New"));
const PeopleList = lazy(() => import("./pages/admin/galleries/PeopleList"));
const PersonDetail = lazy(() => import("./pages/admin/galleries/PersonDetail"));
const Feeds = lazy(() => import("./pages/Feeds"));
const Audio = lazy(() => import("./pages/Audio"));
const AudioDetail = lazy(() => import("./pages/AudioDetail"));
const AudioAdmin = lazy(() => import("./pages/admin/AudioAdmin"));
const AudioEditor = lazy(() => import("./pages/admin/AudioEditor"));
const NotFound = lazy(() => import("./pages/NotFound"));

function FeatureGuard({ feature, children }: { feature: keyof FeatureFlags; children: React.ReactNode }) {
  const { data: config } = useSiteConfig();
  if (!config) return null;
  if (!isFeatureEnabled(feature, config.features)) return <NotFound />;
  return <>{children}</>;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof Error) {
        // Detect 401 — session expired
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          toast.error('Session expired', {
            description: 'Please sign in again to continue.',
            action: {
              label: 'Sign In',
              onClick: () => window.location.href = '/auth/signin',
            },
          });
          return;
        }
        // Skip 404s — these are expected (e.g., navigating to non-existent post)
        if (error.message.includes('404')) return;
        // Show generic error toast for other failures
        toast.error('Something went wrong', {
          description: error.message,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          toast.error('Session expired', {
            description: 'Your session has expired. Please sign in again.',
            action: {
              label: 'Sign In',
              onClick: () => window.location.href = '/auth/signin',
            },
          });
          return;
        }
        toast.error('Save failed', {
          description: error.message,
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2, // Only retry failed requests twice
      retryOnMount: false, // Don't retry when component mounts if query already failed
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      staleTime: 60000, // Consider data fresh for 1 minute
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange={false}
    >
      <CustomThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <ThemeAestheticsController />
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ErrorBoundary fallback={
                <div className="mx-auto max-w-2xl p-8 text-center">
                  <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
                  <p className="text-muted-foreground mb-6">An unexpected error occurred. Please try refreshing the page.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              }>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route element={<Layout />}>
                      <Route path="/" element={<Index />} />
                      <Route path="/galleries" element={<FeatureGuard feature="galleries"><Photos /></FeatureGuard>} />
                      <Route path="/galleries/:slug" element={<FeatureGuard feature="galleries"><GalleryDetail /></FeatureGuard>} />
                      <Route path="/galleries/:gallerySlug/:photoId" element={<FeatureGuard feature="galleries"><PhotoDetail /></FeatureGuard>} />

                      {/* Redirect old /photos routes to /galleries */}
                      <Route path="/photos" element={<Navigate to="/galleries" replace />} />
                      <Route path="/photos/:slug" element={<RedirectGallerySlug />} />
                      <Route path="/photos/:gallerySlug/:photoId" element={<RedirectPhotoDetail />} />

                      <Route path="/blog" element={<FeatureGuard feature="blog"><Writing /></FeatureGuard>} />
                      <Route path="/links" element={<FeatureGuard feature="links"><Links /></FeatureGuard>} />
                      {/* Audio routes (formerly Mixes) */}
                      <Route path="/audio" element={<FeatureGuard feature="mixes"><Audio /></FeatureGuard>} />
                      <Route path="/audio/:slug" element={<FeatureGuard feature="mixes"><AudioDetail /></FeatureGuard>} />

                      {/* Redirects from old /mixes paths */}
                      <Route path="/mixes" element={<Navigate to="/audio" replace />} />
                      <Route path="/mixes/:slug" element={<RedirectMixSlug />} />

                      {/* Unified tag/category routes (all content types) */}
                      <Route path="/tags" element={<TagIndex />} />
                      <Route path="/tags/:slug" element={<TagDetail />} />
                      <Route path="/tags/:slug/page/:page" element={<TagDetail />} />
                      <Route path="/categories" element={<CategoryIndex />} />
                      <Route path="/categories/:slug" element={<CategoryDetail />} />
                      <Route path="/categories/:slug/page/:page" element={<CategoryDetail />} />

                      {/* Blog-specific tag/category routes */}
                      <Route path="/blog/categories" element={<FeatureGuard feature="blog"><CategoryIndex /></FeatureGuard>} />
                      <Route path="/blog/categories/:slug" element={<FeatureGuard feature="blog"><CategoryDetail /></FeatureGuard>} />
                      <Route path="/blog/categories/:slug/page/:page" element={<FeatureGuard feature="blog"><CategoryDetail /></FeatureGuard>} />
                      <Route path="/blog/tags" element={<FeatureGuard feature="blog"><TagIndex /></FeatureGuard>} />
                      <Route path="/blog/tags/:slug" element={<FeatureGuard feature="blog"><TagDetail /></FeatureGuard>} />
                      <Route path="/blog/tags/:slug/page/:page" element={<FeatureGuard feature="blog"><TagDetail /></FeatureGuard>} />

                      <Route path="/blog/:slug" element={<FeatureGuard feature="blog"><PostDetail /></FeatureGuard>} />

                      {/* Legacy /writing → /blog redirects (SPA-side; Caddy also handles these at edge) */}
                      <Route path="/writing" element={<Navigate to="/blog" replace />} />
                      <Route path="/writing/categories" element={<Navigate to="/blog/categories" replace />} />
                      <Route path="/writing/categories/:slug" element={<RedirectWritingCategorySlug />} />
                      <Route path="/writing/categories/:slug/page/:page" element={<RedirectWritingCategoryPage />} />
                      <Route path="/writing/tags" element={<Navigate to="/blog/tags" replace />} />
                      <Route path="/writing/tags/:slug" element={<RedirectWritingTagSlug />} />
                      <Route path="/writing/tags/:slug/page/:page" element={<RedirectWritingTagPage />} />
                      <Route path="/writing/:slug" element={<RedirectWritingSlug />} />
                      <Route path="/archive" element={<Archive />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/feeds" element={<Feeds />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/auth/signin" element={<SignIn />} />
                      <Route path="/auth/signout" element={<SignOut />} />
                      <Route path="/auth/error" element={<Error />} />
                      <Route path="/admin/blog" element={<FeatureGuard feature="blog"><BlogAdmin /></FeatureGuard>} />
                      <Route path="/admin/blog/new" element={<FeatureGuard feature="blog"><BlogEditor /></FeatureGuard>} />
                      <Route path="/admin/blog/edit/:id" element={<FeatureGuard feature="blog"><BlogEditor /></FeatureGuard>} />
                      <Route path="/admin/links" element={<FeatureGuard feature="links"><LinksAdmin /></FeatureGuard>} />
                      <Route path="/admin/galleries" element={<FeatureGuard feature="galleries"><GalleryAdmin /></FeatureGuard>} />
                      <Route path="/admin/galleries/new" element={<FeatureGuard feature="galleries"><NewGallery /></FeatureGuard>} />
                      <Route path="/admin/galleries/edit/:id" element={<FeatureGuard feature="galleries"><GalleryEdit /></FeatureGuard>} />
                      <Route path="/admin/galleries/people" element={<FeatureGuard feature="galleries"><PeopleList /></FeatureGuard>} />
                      <Route path="/admin/galleries/people/:id" element={<FeatureGuard feature="galleries"><PersonDetail /></FeatureGuard>} />
                      {/* Admin audio routes */}
                      <Route path="/admin/audio" element={<FeatureGuard feature="mixes"><AudioAdmin /></FeatureGuard>} />
                      <Route path="/admin/audio/new" element={<FeatureGuard feature="mixes"><AudioEditor /></FeatureGuard>} />
                      <Route path="/admin/audio/edit/:id" element={<FeatureGuard feature="mixes"><AudioEditor /></FeatureGuard>} />
                      <Route path="/admin/mixes" element={<Navigate to="/admin/audio" replace />} />
                      <Route path="/admin/mixes/new" element={<Navigate to="/admin/audio/new" replace />} />
                      <Route path="/admin/mixes/edit/:id" element={<RedirectMixEdit />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
      </CustomThemeProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
